/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useState} from 'react';
import Accordion, {AccordionProps} from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {MaterialExpandMoreIcon} from '@ion/dls/src/icons/MaterialExpandMoreIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {collapseAdjacentBorderMargin} from '../../theme/helpers';
import useThemeContext from '../../theme/useThemeContext';
import {IconSize} from '../../types/types';

// defines the prop definitions for the Accordion
export interface AccordionTypes extends Omit<AccordionProps, 'children'> {
  header: unknown;
  subHeader?: unknown;
  details: unknown;
  expanded?: boolean;
  isCustomStyle?: boolean;
  isFeatureAccordion?: boolean;
  borderBottom?: string;
  border?: string;
  marginTop?: string;
  collapseAdjacentBorder?: boolean;
  disableExpansion?: boolean;
  defaultExpanded?: boolean;
  padding?: string;
  marginBottom?: string
  onExpand?: (expanded: boolean) => void;
  isCondensedVariant?: boolean;
  margin?: string;
  backgroundColor?: string;
}

interface AccordionStyles {
  borderBottom?: string;
  border?: string;
  marginTop?: string;
  collapseAdjacentBorder?: boolean;
  padding?: string;
  marginBottom?: string;
  isCondensedVariant?: boolean;
  backgroundColor?: string;
  isCurrentTheme?: boolean;
  isCustomStyle?: boolean;
  isFeatureAccordion?: boolean;
  margin?: string;
}

// defines the styles used by the Accordion
const useStyles = makeStyles(theme => ({
  wrapperRoot : {
    width            : '100%',
    boxShadow        : '0px 0px !important',
    borderRadius     : '0px !important',
    backgroundColor  : 'none !important',
    padding          : '0px',
    marginBottom     : ({marginBottom}: AccordionStyles) => marginBottom || '0px',
    margin           : ({margin}: AccordionStyles) => margin,
    '&.Mui-expanded' : {
      margin : ({isCurrentTheme, margin}: AccordionStyles) => (isCurrentTheme ? (margin || '0px') : undefined)
    },
    '&:before' : {
      display : ({isCurrentTheme}: AccordionStyles) => (isCurrentTheme ? 'none' : undefined)
    }
  },
  summaryContent : {
    margin : '0 !important',
    width  : ({isCustomStyle}: AccordionStyles) => isCustomStyle ? '100%' : 'auto'
  },
  condensedSummaryContent : {
    margin      : '0 !important',
    maxHeight   : 48,
    paddingLeft : '0px !important',
    width       : ({isCustomStyle}: AccordionStyles) => isCustomStyle ? '100%' : 'auto'
  },
  detailsRoot : {
    display         : 'block',
    borderTop       : ({isCurrentTheme}: AccordionStyles) => (isCurrentTheme ? 'none' : `solid 1px ${dlsColor('grey5')}`),
    backgroundColor : ({backgroundColor}: AccordionStyles) => backgroundColor || dlsColor('white'),
    padding         : ({padding, isCurrentTheme}: AccordionStyles) => (
      padding || (isCurrentTheme ? '8px 16px 24px 16px' : '10px 0px 25px')
    ),
    marginTop : ({collapseAdjacentBorder, marginTop}: AccordionStyles) =>
      marginTop || collapseAdjacentBorderMargin(collapseAdjacentBorder)
  },
  expanded : {
    flexDirection : ({isFeatureAccordion}: AccordionStyles) => isFeatureAccordion ? 'row-reverse' : 'inherit',
    padding       : ({isFeatureAccordion}: AccordionStyles) => isFeatureAccordion ? '0 0px' : '0 15px',
    border        : ({border}: AccordionStyles) => border || undefined,
    borderBottom  : ({border, borderBottom, isCurrentTheme}: AccordionStyles) => (
      border ? undefined : (borderBottom || (isCurrentTheme ? 'none' : `solid 1px ${dlsColor('grey6')}`))
    ),
    marginTop : ({collapseAdjacentBorder}: AccordionStyles) =>
      collapseAdjacentBorderMargin(collapseAdjacentBorder),
    '&$expanded' : {
      padding   : ({isFeatureAccordion}: AccordionStyles) => isFeatureAccordion ? '0 0px' : '0 15px',
      minHeight : ({isCondensedVariant, isCurrentTheme}: AccordionStyles) => (
        isCondensedVariant ? (isCurrentTheme ? '56px' : '48px') : (isCurrentTheme ? '56px' : '64px')
      )
    }
  },
  disabledState : {
    '& .MuiAccordionSummary-expandIcon' : {
      paddingLeft : '0px !important',
      display     : 'none'
    },
    pointerEvents : 'none'
  },
  expandIconRotation : {
    transform                : 'rotate(180deg)',
    '&$summaryExpandedState' : {
      transform : 'rotate(0deg)'
    }
  },
  summaryExpandedState : {}
}));

export const getAccordionWrapperStyles = (
  borderStyle: string = `1px solid ${dlsColor('grey5')}`
) => ({
  accordionWrapper : {
    borderBottom : borderStyle
  }
});

/**
 * @Accordion
 *
 * Defines the Accordion component. This component is reusable and can be custom
 * rendered with props.
 *
 * `border` applies a full border (all sides) to the summary row, taking
 * precedence over `borderBottom` when both are passed - the two are meant to
 * be mutually exclusive per usage. `collapseAdjacentBorder` opts the summary
 * and details rows into a `-1px` margin offset so their own border doesn't
 * visibly double up against whatever border-having element precedes them;
 * pass it alongside `border` when the caller's layout stacks bordered rows
 * against each other.
 *
 * ```typescript
 *
 *   import Accordion from '@ion/dls/src/components/accordion/Accordion';
 *   <Accordion children=''
 *     header={}
 *     details={}
 *     border='1px solid #E0E0E0'
 *     collapseAdjacentBorder />
 * ```
 */
const AccordionWrapper = ({
  header,
  subHeader,
  details,
  expanded,
  borderBottom,
  border,
  marginTop,
  collapseAdjacentBorder,
  defaultExpanded,
  padding,
  marginBottom,
  disableExpansion,
  onExpand,
  isCondensedVariant,
  className,
  margin,
  backgroundColor,
  isCustomStyle = false,
  isFeatureAccordion = false
}: AccordionTypes) => {
  const {
    isCurrent : isCurrentTheme
  } = useThemeContext();

  const classes = useStyles({
    isCurrentTheme,
    isCondensedVariant,
    backgroundColor,
    borderBottom,
    border,
    marginTop,
    collapseAdjacentBorder,
    marginBottom,
    padding,
    isCustomStyle,
    isFeatureAccordion,
    margin
  });

  const [isExpanded, setIsExpanded] = useState(false);

  const onHandleChange = useCallback(function(event, expanded) {
    onExpand?.(expanded);
    setIsExpanded(expanded);
  }, []);

  return (
    <Accordion classes={{root: classes.wrapperRoot}}
      defaultExpanded={defaultExpanded || expanded || isExpanded}
      expanded={expanded}
      onChange={onHandleChange}>
      <AccordionSummary expandIcon={<MaterialExpandMoreIcon size={IconSize.small} color={dlsColor('grey2')} />}
        classes={{
          content : isCondensedVariant ? classes.condensedSummaryContent : classes.summaryContent,
          root    : classnames(className, disableExpansion ? classes.disabledState : classes.expanded),
          ...(isCurrentTheme && {
            expandIcon : classes.expandIconRotation,
            expanded   : classes.summaryExpandedState
          })
        }}>
        <>
          {header}
          {subHeader}
        </>
      </AccordionSummary>
      <AccordionDetails classes={{root: classes.detailsRoot}}>
        {details}
      </AccordionDetails>
    </Accordion>
  );
};

export default AccordionWrapper;
